import React, { useEffect, useId, useRef } from "react";
import { ScaleLoader } from "react-spinners";
import AccessibleDialog from "../UI/Modals/AccessibleDialog/AccessibleDialog";
import OrderItemPicker from "./OrderItemPicker";
import OrderItemsList from "./OrderItemsList";
import DeliverySection from "./DeliverySection";
import {
  estimatedItemsSubtotal,
  useConsultOrderContext,
} from "../../contexts/ConsultOrderContext";
import {
  draftDeliveryFee,
  ConsultOrderValidationErrors,
} from "../../utils/consultOrderPayload";
import { ConsultOrderPhase } from "../../hooks/useConsultOrderLifecycle";
import { CnCOrderResponse } from "../../types/api";
import styles from "./ConsultOrderFormModal.module.scss";

// Fields in top-to-bottom visual order, matched to their `data-consult-field`
// markers. A failed submit focuses (and scrolls to) the FIRST field in this order
// that has a validation error, so the pharmacist is taken straight to the problem.
const FIELD_FOCUS_ORDER: (keyof ConsultOrderValidationErrors)[] = [
  "items",
  "address",
  "delivery",
  "deliveryTimeSlot",
  "personalId",
  "firstName",
  "phoneNumber",
];

const focusFirstInvalidField = (
  errors: ConsultOrderValidationErrors,
  container: HTMLElement | null
): void => {
  const scope: ParentNode = container ?? document;
  for (const key of FIELD_FOCUS_ORDER) {
    if (!errors[key]) {
      continue;
    }
    const el = scope.querySelector<HTMLElement>(
      `[data-consult-field="${key}"]`
    );
    if (el) {
      el.focus();
      if (typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
      return;
    }
  }
};

// ITAM-2583: the consult order form. It is purely presentational over the shared
// ConsultOrderContext (draft + prefill) — item/delivery editing lives in the
// sibling sections; this shell composes them, renders customer fields + an
// ESTIMATED review, and wires the lifecycle callbacks (submit/close/success).
// The 201 response is authoritative, so every total here is labelled "procenjeno".

export interface ConsultOrderFormModalProps {
  isOpen: boolean;
  phase: ConsultOrderPhase;
  graceActive: boolean;
  consultEnded: boolean;
  createdOrder: CnCOrderResponse | null;
  submitError: string | null;
  validationErrors: ConsultOrderValidationErrors;
  /** Bumped on each failed submit attempt; drives focus to the first invalid field. */
  submitAttemptId: number;
  lineErrors: Record<number, string>;
  onSubmit: () => void;
  onClose: () => void;
  onCloseSuccess: () => void;
  onDismissError: () => void;
}

const TITLE_ID = "consult-order-form-title";

const formatAmount = (value: number): string =>
  `${value.toLocaleString("sr-RS", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} RSD`;

const ConsultOrderFormModal: React.FC<ConsultOrderFormModalProps> = ({
  isOpen,
  phase,
  graceActive,
  consultEnded,
  createdOrder,
  submitError,
  validationErrors,
  submitAttemptId,
  lineErrors,
  onSubmit,
  onClose,
  onCloseSuccess,
  onDismissError,
}) => {
  const { prefill, draft, updateDraft } = useConsultOrderContext();

  const closeRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const reactId = useId();
  const firstNameId = `${reactId}-first-name`;
  const secondNameId = `${reactId}-second-name`;
  const phoneId = `${reactId}-phone`;
  const noteId = `${reactId}-note`;
  const firstNameErrId = `${firstNameId}-error`;
  const phoneErrId = `${phoneId}-error`;

  const callerName =
    [prefill?.user.firstName, prefill?.user.secondName]
      .filter((part) => part && part.trim())
      .join(" ")
      .trim() ||
    prefill?.user.username ||
    "kupca";

  const isSuccess = phase === "SUCCESS";
  const isSubmitting = phase === "SUBMITTING";

  // The button stays clickable so a click always RUNS validation and surfaces the
  // field errors (it is only inert while a submit is actually in flight). The
  // lifecycle submit() still validates before any POST — an invalid draft never
  // reaches the network.
  const submitDisabled = isSubmitting;

  // On a failed submit attempt (nonce bump), move focus to the first invalid field.
  useEffect(() => {
    if (submitAttemptId <= 0) {
      return;
    }
    focusFirstInvalidField(validationErrors, bodyRef.current);
    // Intentionally keyed to the attempt nonce only: re-focus once per attempt,
    // never while the pharmacist is editing (validationErrors changes with it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitAttemptId]);

  // react-modal applies initialFocusRef only via onAfterOpen (the open
  // transition). The FORM/SUBMITTING -> SUCCESS swap happens with the modal
  // already open — the focused submit button unmounts — so move focus to the
  // success close button here or keyboard/screen-reader users land on <body>.
  useEffect(() => {
    if (isSuccess) {
      closeRef.current?.focus();
    }
  }, [isSuccess]);

  const isPickup = draft.fulfillment === "PICKUP";
  // A loyalty-paid delivery fee is settled in points, not dinars — keep it out
  // of the RSD summary entirely (no fee row, excluded from the total).
  const paidWithPoints = !isPickup && draft.paymentType === "LOYALTY_POINTS";
  const subtotal = estimatedItemsSubtotal(draft.items);
  const deliveryFee = paidWithPoints ? 0 : draftDeliveryFee(draft);
  const estimatedTotal = subtotal + deliveryFee;

  const handleRequestClose = () => {
    if (isSuccess) {
      onCloseSuccess();
    } else {
      onClose();
    }
  };

  return (
    <AccessibleDialog
      isOpen={isOpen}
      onRequestClose={handleRequestClose}
      titleId={TITLE_ID}
      initialFocusRef={isSuccess ? closeRef : undefined}
      maxWidth={760}
      className={styles.dialogInner}
    >
      <div className={styles.container}>
        <header className={styles.header}>
          <h2 id={TITLE_ID} className={styles.title}>
            Nova porudžbina — {callerName}
          </h2>
        </header>

        {isSuccess ? (
          <div className={styles.successBody} role="status">
            {createdOrder ? (
              <>
                <p className={styles.successHeadline}>
                  Porudžbina #{createdOrder.id} je kreirana.
                </p>
                <dl className={styles.successMeta}>
                  <div className={styles.successRow}>
                    <dt>Ukupno</dt>
                    <dd>
                      {createdOrder.totalPriceWithDelivery != null
                        ? formatAmount(createdOrder.totalPriceWithDelivery)
                        : "—"}
                    </dd>
                  </div>
                  <div className={styles.successRow}>
                    <dt>Broj artikala</dt>
                    <dd>{createdOrder.items?.length ?? draft.items.length}</dd>
                  </div>
                </dl>
              </>
            ) : (
              <p className={styles.successHeadline}>
                {submitError ?? "Porudžbina je već kreirana."}
              </p>
            )}
            <div className={styles.footerActions}>
              <button
                type="button"
                ref={closeRef}
                className={styles.primary}
                onClick={onCloseSuccess}
              >
                Zatvori
              </button>
            </div>
          </div>
        ) : (
          <>
            {(consultEnded || graceActive) && (
              <div className={styles.graceBanner} role="status">
                Konsultacija je završena — porudžbinu možete poslati još kratko.
              </div>
            )}
            {submitError && (
              <div className={styles.errorBanner} role="alert">
                <span className={styles.errorText}>{submitError}</span>
                <button
                  type="button"
                  className={styles.errorDismiss}
                  onClick={onDismissError}
                  aria-label="Zatvori poruku o grešci"
                >
                  Zatvori
                </button>
              </div>
            )}

            <div className={styles.body} ref={bodyRef}>
              <section className={styles.section} aria-label="Artikli">
                <h3 className={styles.sectionTitle}>Artikli</h3>
                <OrderItemPicker />
                <OrderItemsList lineErrors={lineErrors} />
              </section>

              <section
                className={styles.section}
                aria-label="Način preuzimanja i dostava"
              >
                <h3 className={styles.sectionTitle}>
                  Način preuzimanja i dostava
                </h3>
                <DeliverySection errors={validationErrors} />
              </section>

              <section className={styles.section} aria-label="Podaci kupca">
                <h3 className={styles.sectionTitle}>Podaci kupca</h3>
                <div className={styles.fieldGrid}>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor={firstNameId}>
                      Ime
                    </label>
                    <input
                      id={firstNameId}
                      className={styles.input}
                      data-consult-field="firstName"
                      type="text"
                      value={draft.firstName}
                      onChange={(e) =>
                        updateDraft({ firstName: e.target.value })
                      }
                      aria-invalid={validationErrors.firstName ? true : undefined}
                      aria-describedby={
                        validationErrors.firstName ? firstNameErrId : undefined
                      }
                    />
                    {validationErrors.firstName && (
                      <span
                        id={firstNameErrId}
                        className={styles.fieldError}
                        role="alert"
                      >
                        {validationErrors.firstName}
                      </span>
                    )}
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label} htmlFor={secondNameId}>
                      Prezime
                    </label>
                    <input
                      id={secondNameId}
                      className={styles.input}
                      type="text"
                      value={draft.secondName}
                      onChange={(e) =>
                        updateDraft({ secondName: e.target.value })
                      }
                    />
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label} htmlFor={phoneId}>
                      Broj telefona
                    </label>
                    <input
                      id={phoneId}
                      className={styles.input}
                      data-consult-field="phoneNumber"
                      type="tel"
                      value={draft.phoneNumber}
                      onChange={(e) =>
                        updateDraft({ phoneNumber: e.target.value })
                      }
                      aria-invalid={
                        validationErrors.phoneNumber ? true : undefined
                      }
                      aria-describedby={
                        validationErrors.phoneNumber ? phoneErrId : undefined
                      }
                    />
                    {validationErrors.phoneNumber && (
                      <span
                        id={phoneErrId}
                        className={styles.fieldError}
                        role="alert"
                      >
                        {validationErrors.phoneNumber}
                      </span>
                    )}
                  </div>

                  <div className={`${styles.field} ${styles.fieldFull}`}>
                    <label className={styles.label} htmlFor={noteId}>
                      Napomena
                    </label>
                    <textarea
                      id={noteId}
                      className={styles.textarea}
                      rows={2}
                      value={draft.note}
                      onChange={(e) => updateDraft({ note: e.target.value })}
                    />
                  </div>
                </div>
              </section>

              <section className={styles.section} aria-label="Pregled">
                <h3 className={styles.sectionTitle}>Pregled</h3>
                <dl className={styles.summary}>
                  <div className={styles.summaryRow}>
                    <dt>Artikli (procenjeno)</dt>
                    <dd>{formatAmount(subtotal)}</dd>
                  </div>
                  {!isPickup && !paidWithPoints && (
                    <div className={styles.summaryRow}>
                      <dt>Dostava (orijentaciono)</dt>
                      <dd>{formatAmount(deliveryFee)}</dd>
                    </div>
                  )}
                  <div
                    className={`${styles.summaryRow} ${styles.summaryTotal}`}
                  >
                    <dt>Ukupno (procenjeno)</dt>
                    <dd>{formatAmount(estimatedTotal)}</dd>
                  </div>
                </dl>
                <p className={styles.summaryNote}>
                  Iznosi su orijentacioni — konačnu cenu potvrđuje kreirana
                  porudžbina.
                </p>
              </section>
            </div>

            <footer className={styles.footer}>
              <button
                type="button"
                className={styles.secondary}
                onClick={onClose}
              >
                Otkaži
              </button>
              <button
                type="button"
                className={styles.primary}
                onClick={onSubmit}
                disabled={submitDisabled}
              >
                {isSubmitting ? (
                  <span className={styles.submitSpinner}>
                    <ScaleLoader color="#ffffff" height={14} width={3} />
                  </span>
                ) : (
                  "Pošalji porudžbinu"
                )}
              </button>
            </footer>
          </>
        )}
      </div>
    </AccessibleDialog>
  );
};

export default ConsultOrderFormModal;
